import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Crown, Heart, Zap, Sparkles } from "lucide-react";
import { toast } from "sonner";
import GameCanvas3D from "@/game3d/GameCanvas3D";
import GameOverDialog from "@/components/GameOverDialog";
import { seedFromString, todayUtcKey } from "@/lib/seededRng";
import { getCurrentSkin } from "@/lib/skins";
import { bumpStreakOnDailyRun } from "@/lib/streak";
import { makeRecorder, fetchTopGhost } from "@/lib/replay";
import { fetchWeeklyChampion, isChampionName } from "@/lib/champion";
import { hasExtraLife, consumeExtraLife, grantExtraLife, didBeatGhost } from "@/lib/raceGhost";
import { checkAchievements } from "@/lib/achievements";
import { getIsSeason } from "@/lib/pro";
import { recordPlayDay } from "@/lib/seasonRewards";
import { syncPlayerState, saveActiveRun, clearActiveRun } from "@/lib/playerSync";
import { awardChipsForRun } from "@/lib/chips";
import { weatherForRun } from "@/lib/weather";

export default function Play3DPage() {
  const { level } = useParams();
  const nav = useNavigate();
  const [endState, setEndState] = useState(null);
  const [runKey, setRunKey] = useState(0);
  const [ghost, setGhost] = useState(null);
  const [champion, setChampion] = useState(null);

  const isDaily = level === "daily";
  const validLevel =
    level === "underground" ? "underground" :
    level === "rooftop" ? "rooftop" :
    level === "highway" ? "highway" :
    level === "snowstorm" ? "snowstorm" :
    level === "tunnels" ? "tunnels" :
    level === "beach" ? "beach" :
    level === "neon_rain" ? "neon_rain" : "outdoor";
  const daySeed = isDaily ? seedFromString(todayUtcKey()) : null;
  const submitLevel = isDaily ? "daily" : validLevel;
  const skin = getCurrentSkin();

  // Season Pass perks
  const seasonActive = getIsSeason();
  const seasonExtraLife = seasonActive;
  const seasonScoreMultiplier = seasonActive ? 2 : 1;
  const raceBonusExtraLife = isDaily && hasExtraLife();
  const extraLifeForThisRun = seasonExtraLife || raceBonusExtraLife;
  const [showedExtraLifeBanner, setShowedExtraLifeBanner] = useState(false);
  const runWeather = seasonActive ? weatherForRun() : null;

  const recorderRef = useRef(makeRecorder());

  useEffect(() => {
    saveActiveRun({ level: submitLevel });
    return () => { clearActiveRun(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitLevel, runKey]);

  useEffect(() => {
    if (!isDaily) return;
    fetchTopGhost().then((g) => {
      if (g && g.replay && g.replay.length > 0) setGhost(g);
    });
    fetchWeeklyChampion().then((c) => {
      if (c && c.player_name) setChampion(c);
    });
    if (hasExtraLife() && !showedExtraLifeBanner) {
      consumeExtraLife();
      setShowedExtraLifeBanner(true);
      toast.success("+1 LIFE ACTIVE · reward for beating yesterday's champion");
    }
  }, [isDaily, showedExtraLifeBanner]);

  const finish = (r, won) => {
    const samples = recorderRef.current.getSamples().slice();
    let beatGhost = false;
    recordPlayDay();
    awardChipsForRun({ score: r.score, won });
    if (isDaily) {
      const bump = bumpStreakOnDailyRun();
      if (bump && bump.freezeUsed) toast.success("STREAK FROZEN · you used a Streak Freeze");
      if (ghost && didBeatGhost(r.score, r.time, ghost)) {
        beatGhost = true;
        grantExtraLife();
        toast.success(`YOU BEAT ${ghost.player_name}! +1 LIFE NEXT RUN`);
      }
    }
    const newly = checkAchievements({ score: r.score, time: r.time, level: submitLevel, won, beatGhost });
    for (const a of newly) toast.success(`ACHIEVEMENT · ${a.name}`);
    setEndState({ ...r, won, level: submitLevel, replay: isDaily ? samples : undefined });
    clearActiveRun();
    syncPlayerState();
  };

  const retry = () => {
    recorderRef.current.clear();
    setEndState(null);
    setRunKey((k) => k + 1);
  };

  return (
    <div className="min-h-screen w-full grid-bg py-6" data-testid="play3d-page">
      <GameCanvas3D
        key={runKey + submitLevel}
        level={validLevel}
        seed={daySeed}
        skinColor={skin.color}
        skinGlow={skin.glow}
        ghostReplay={isDaily && ghost ? ghost.replay : null}
        onSample={isDaily ? recorderRef.current.onSample : null}
        extraLife={extraLifeForThisRun}
        scoreMultiplier={seasonScoreMultiplier}
        weather={runWeather}
        onExit={() => nav("/")}
        onGameOver={(r) => finish(r, false)}
        onWin={(r) => finish(r, true)}
      />
      {isDaily && ghost && ghost.player_name && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-1.5 border border-white/30 bg-black/60 backdrop-blur-md text-[10px] tracking-[0.3em] text-white/80"
          data-testid="ghost-indicator-3d"
        >
          <span className="w-2 h-2 rounded-full bg-white/70" style={{ boxShadow: "0 0 8px rgba(255,255,255,0.7)" }} />
          GHOST · {ghost.player_name}
          {isChampionName(champion, ghost.player_name) && (
            <Crown size={12} className="text-[#FFD400]" style={{ filter: "drop-shadow(0 0 6px rgba(255,212,0,0.7))" }} />
          )}
          · {String(ghost.score).padStart(6, "0")} · {(ghost.survival_time || 0).toFixed(1)}s
          <span className="ml-2 pl-2 border-l border-white/20 flex items-center gap-1 text-[#FFD400]">
            <Zap size={11} /> BEAT FOR +1 LIFE
          </span>
        </div>
      )}
      {extraLifeForThisRun && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-1.5 border border-[#FF0055] bg-black/60 backdrop-blur-md text-[10px] tracking-[0.3em] text-[#FF0055]"
          data-testid="extra-life-indicator-3d"
          style={{ boxShadow: "0 0 16px rgba(255,0,85,0.35)" }}
        >
          <Heart size={11} className="fill-[#FF0055]" /> +1 LIFE ACTIVE
        </div>
      )}
      {seasonActive && (
        <div
          className="fixed top-28 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-1.5 border border-[#F0ABFC] bg-black/60 backdrop-blur-md text-[10px] tracking-[0.3em] text-[#F0ABFC]"
          data-testid="season-perk-indicator-3d"
          style={{ boxShadow: "0 0 16px rgba(240,171,252,0.35)" }}
        >
          <Sparkles size={11} /> SEASON PASS · 2× SCORE
        </div>
      )}
      <GameOverDialog
        open={!!endState}
        result={endState}
        onRetry={retry}
        onMenu={() => {
          setEndState(null);
          nav("/");
        }}
        onLeaderboard={() => {
          setEndState(null);
          nav("/leaderboard");
        }}
      />
    </div>
  );
}
